import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, CloudUpload, XCircle, CheckCircle } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const EditProductModal = ({
  isEditing,
  setIsEditing,
  editingProduct,
  setEditingProduct,
  handleUpdate,
  previewImage,
  setPreviewImage,
  isMarmuristPrice,
}) => {
  const [successMessage, setSuccessMessage] = useState("");
  const { darkMode } = useTheme();

  if (!isEditing || !editingProduct) return null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreviewImage(URL.createObjectURL(file));
      setEditingProduct((prev) => ({
        ...prev,
        imageFile: file,
        isImageDeleted: false,
      }));
    }
  };

  const handleRemoveImage = () => {
    setPreviewImage(null);
    setEditingProduct((prev) => ({
      ...prev,
      imageFile: null,
      imageUrl: "",
      isImageDeleted: true,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleUpdate();

    setSuccessMessage("");
    setIsEditing(false);
  };

  const handlePriceChange = (e) => {
    const newPrice = e.target.value;
    setEditingProduct((prev) => ({
      ...prev,
      ...(isMarmuristPrice
        ? { priceMarmurist: newPrice } // Ha Marmurist nézetben vagyunk, ezt módosítja
        : { price: newPrice }), // Ha Final nézetben vagyunk, ezt módosítja
    }));
  };
  return (
    <>
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg text-center z-50"
          >
            {successMessage}
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={`fixed inset-0 flex items-center justify-center bg-opacity-80 ${darkMode ? "bg-black" : "bg-gray-500"
          }`}
        onClick={(e) => e.target === e.currentTarget && setIsEditing(false)}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className={`p-6 rounded-3xl shadow-xl w-[600px] relative ${darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setIsEditing(false)}
            className="absolute top-2 right-3 text-gray-400 hover:text-red-500 transition text-2xl"
          >
            ✖
          </button>

          <h3
            className={`text-2xl font-semibold mb-4 text-center ${darkMode ? "text-blue-400" : "text-blue-600"
              }`}
          >
            ✏️ Editare produs
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block font-semibold">Numele produsului:</label>
            <input
              type="text"
              value={editingProduct.name}
              onChange={(e) =>
                setEditingProduct({ ...editingProduct, name: e.target.value })
              }
              className={`w-full p-2 border rounded-3xl ${darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
                }`}
              required
            />

            <label className="block font-semibold">
              {isMarmuristPrice ? "Preț Marmurist (EUR)" : "Preț Final (EUR)"}:
            </label>
            <input
              type="text"
              value={
                isMarmuristPrice
                  ? editingProduct.priceMarmurist
                  : editingProduct.price
              }
              onChange={handlePriceChange}
              className={`w-full p-2 border rounded-3xl ${darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
                }`}
              min="0"
              required
            />

            <label className="block font-semibold">Preț de achiziție (EUR):</label>
            <input
              type="number"
              step="0.01"
              value={editingProduct.costPrice ?? ""}
              onChange={(e) =>
                setEditingProduct({
                  ...editingProduct,
                  costPrice: e.target.value,
                })
              }
              className={`w-full p-2 border rounded-3xl ${darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
                }`}
              min="0"
            />

            <label className="block font-semibold">Stoc disponibil:</label>
            <input
              type="number"
              step="0.01"
              value={editingProduct.availableStock}
              onChange={(e) =>
                setEditingProduct({
                  ...editingProduct,
                  availableStock: e.target.value,
                })
              }
              className={`w-full p-2 border rounded-3xl ${darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-black"
                }`}
              min="0"
            />

            <label className="block font-semibold">Imagine:</label>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="fileInput"
              />
              <label
                htmlFor="fileInput"
                className={`flex items-center justify-center gap-2 py-4 px-4 rounded-3xl cursor-pointer transition ${darkMode
                    ? "bg-blue-700 hover:bg-blue-600 text-white"
                    : "bg-blue-600 hover:bg-blue-500 text-white"
                  }`}
              >
                <CloudUpload size={25} />
                <span className="text-xl font-semibold">
                  {editingProduct.imageFile
                    ? "Schimbă imaginea"
                    : "Alege fișier"}
                </span>
              </label>
            </div>

            {(previewImage || editingProduct.imageUrl) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="relative mt-4"
              >
                <img
                  src={previewImage || editingProduct.imageUrl}
                  alt="Previzualizare"
                  className="w-full h-40 object-cover rounded shadow-md"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className={`absolute top-2 right-2 p-1 rounded-full transition ${darkMode
                      ? "bg-red-700 hover:bg-red-600 text-white"
                      : "bg-red-600 hover:bg-red-500 text-white"
                    }`}
                >
                  <Trash2 size={18} />
                </button>
              </motion.div>
            )}

            <div className="flex justify-between mt-4 gap-4">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className={`w-full px-5 py-5 rounded-3xl transition flex items-center justify-center gap-2 text-lg font-semibold ${darkMode
                    ? "bg-gray-600 text-white hover:bg-gray-500"
                    : "bg-gray-500 text-white hover:bg-gray-400"
                  }`}
              >
                <XCircle size={24} /> Anulare
              </button>

              <button
                type="submit"
                className={`w-full px-5 py-5 rounded-3xl transition flex items-center justify-center gap-2 text-lg font-semibold ${darkMode
                    ? "bg-green-700 text-white hover:bg-green-600"
                    : "bg-green-600 text-white hover:bg-green-500"
                  }`}
              >
                <CheckCircle size={24} /> Salvează
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </>
  );
};

export default EditProductModal;
